import type { popstore } from '@ps/types';

/**
 * Our image URLs carry two out-of-band embellishments the CDN/browser ignore
 * but the app reads:
 *
 * - **Metadata** in a `_m` (current) or `_metadata` (legacy) query param, whose
 *   value is itself a URL-encoded querystring: `i` id, `b` brightness, `w`
 *   width, `h` height, `s` sizeInBytes, `f` filename. Encoded by
 *   `imageUrlWithMetadata` (PopshopRN `flows/bio/utils`).
 * - **BlurHash** in the URL fragment (`#<encodeURIComponent(hash)>`).
 *
 * These break next/image's optimizer (it rejects arbitrary query strings), so
 * consumers extract the values with the readers here, then hand next/image the
 * {@link cleanImageUrl} — stripped of both — which optimizes fine.
 */

const METADATA_PARAM = '_m';
const LEGACY_METADATA_PARAM = '_metadata';

/** Decode the embedded `popstore.ImageMetadata`, if any. */
export function imageMetadataFromUrl(
  uri: string | undefined,
): popstore.ImageMetadata | undefined {
  if (uri == null) return undefined;
  try {
    const url = new URL(uri);
    const metadata =
      url.searchParams.get(LEGACY_METADATA_PARAM) ??
      url.searchParams.get(METADATA_PARAM);
    if (!metadata) return undefined;
    const params = new URLSearchParams(metadata);
    const brightness = params.get('b') ?? params.get('brightness');
    const width = params.get('w');
    const height = params.get('h');
    const size = params.get('s');
    const filename = params.get('f');
    const id = params.get('i') ?? undefined;
    return {
      id,
      width: width ? parseInt(width, 10) : undefined,
      height: height ? parseInt(height, 10) : undefined,
      brightness: brightness ? parseFloat(brightness) : undefined,
      sizeInBytes: size ? parseInt(size, 10) : undefined,
      filename: filename ?? undefined,
    };
  } catch {
    return undefined;
  }
}

/**
 * Embed `popstore.ImageMetadata` into an image URL as the `_m` query param,
 * mirroring PopshopRN's `imageUrlWithMetadata` (`flows/bio/utils`) — the writer
 * counterpart to {@link imageMetadataFromUrl}. Used so an uploaded asset carries
 * its original `filename` + `sizeInBytes` (the CDN key is opaque), and they
 * survive persistence into edit. Returns the URL unchanged when there's no
 * metadata to attach, or when `uri` isn't an absolute URL.
 */
export function imageUrlWithMetadata(
  uri: string,
  metadata: popstore.ImageMetadata,
): string {
  try {
    const url = new URL(uri);
    const params = new URLSearchParams();
    if (metadata.id) params.set('i', metadata.id);
    if (metadata.brightness != null)
      params.set('b', metadata.brightness.toString());
    if (metadata.width != null) params.set('w', metadata.width.toString());
    if (metadata.height != null) params.set('h', metadata.height.toString());
    if (metadata.sizeInBytes != null)
      params.set('s', metadata.sizeInBytes.toString());
    if (metadata.filename) params.set('f', metadata.filename);
    const metadataString = params.toString();
    if (metadataString.length > 0) {
      url.searchParams.set(METADATA_PARAM, metadataString);
    }
    return url.toString();
  } catch {
    return uri;
  }
}

/** The BlurHash from the URL fragment, decoded (the encoder percent-encodes it). */
export function blurhashFromUrl(uri: string | undefined): string | undefined {
  if (uri == null) return undefined;
  const hashIndex = uri.indexOf('#');
  if (hashIndex < 0) return undefined;
  const fragment = uri.slice(hashIndex + 1);
  if (!fragment) return undefined;
  try {
    return decodeURIComponent(fragment);
  } catch {
    return fragment;
  }
}

/**
 * Strip the metadata params (`_m` / `_metadata`) and the BlurHash fragment,
 * leaving the plain asset URL to hand next/image. Fragment removal is by string
 * slice first so relative URLs (which `new URL` rejects) still get cleaned.
 */
export function cleanImageUrl(uri: string): string {
  const hashIndex = uri.indexOf('#');
  const withoutHash = hashIndex >= 0 ? uri.slice(0, hashIndex) : uri;
  try {
    const url = new URL(withoutHash);
    url.searchParams.delete(METADATA_PARAM);
    url.searchParams.delete(LEGACY_METADATA_PARAM);
    return url.toString();
  } catch {
    return withoutHash;
  }
}
